import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Heart, Compass, Scale } from "lucide-react";
import { Divider } from "@/components/Divider";
import { SectionHeader } from "@/components/SectionHeader";

export default function About() {
  return (
    <div data-testid="page-about">
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-4">About DW Investment Group</p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black tracking-tight">
              Built on trust.
              <br/>Closed on time.
            </h1>
            <Divider className="my-6" />
            <p className="font-sub italic text-xl text-gray-700 max-w-2xl">
              We've spent over two decades helping families, veterans, and investors move
              into the homes that change their lives.
            </p>
            <p className="text-base text-gray-700 mt-5 leading-relaxed max-w-2xl">
              DW Investment Group is a mortgage origination practice grounded in one
              principle: the mortgage process should feel like a partnership, not a
              transaction. We sit on your side of the table, explain every line, and stay
              accessible from application to closing — and well beyond.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img
              src="https://images.pexels.com/photos/8441812/pexels-photo-8441812.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=940"
              alt="Loan originator team meeting with client"
              className="w-full h-[460px] object-cover rounded-sm shadow-lg"
            />
          </div>
        </div>
      </section>

      <div className="brand-divider-full" />

      {/* VALUES */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="What We Stand For" title="Four values, no exceptions" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { Icon: Heart, t: "Client First", s: "Your goals shape the loan, not the other way around." },
              { Icon: Scale, t: "Honest Math", s: "Transparent rates, fees, and closing costs — always." },
              { Icon: Compass, t: "Expert Guidance", s: "Twenty plus years of mortgage and credit experience." },
              { Icon: CheckCircle2, t: "On-Time Closing", s: "We hit the dates we promise. Period." },
            ].map(({ Icon, t, s }) => (
              <div key={t} className="bg-white p-7 border border-gray-200 rounded-sm border-t-4 border-t-[#00A651]">
                <Icon className="text-[#F26522]" size={28} />
                <h3 className="font-heading font-bold text-lg text-black mt-4">{t}</h3>
                <p className="text-sm text-gray-600 mt-2 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose-lg">
          <p className="text-xs uppercase tracking-[0.3em] font-bold text-[#00A651] mb-3">Our Story</p>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl text-black">
            A practice grounded in service.
          </h2>
          <Divider className="my-5" />
          <p className="text-gray-700 leading-relaxed">
            DW Investment Group began with a simple observation: too many borrowers were
            handed a stack of paperwork and left to decipher it alone. We set out to
            change that — one closing at a time. Today we serve buyers and homeowners
            across the country, offering Conventional, FHA, VA, USDA, Jumbo, and
            Refinance programs through a personal originator who answers the phone.
          </p>
          <p className="text-gray-700 leading-relaxed mt-4">
            We're proud Equal Housing Lenders, fully licensed and NMLS-registered.
            Compliance and clarity aren't checkboxes for us — they're the way we work.
          </p>
        </div>
      </section>

      {/* NMLS */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#E88C2F] font-bold mb-3">Licensing</p>
            <h3 className="font-heading font-bold text-2xl">NMLS Compliance</h3>
            <p className="font-sub italic text-gray-400 mt-2">Fully licensed and disclosed.</p>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-6">
            {[
              { k: "Company NMLS#", v: "000000" },
              { k: "Originator NMLS#", v: "000000" },
              { k: "State Licenses", v: "Available on request" },
              { k: "Disclosures", v: "Equal Housing Lender" },
            ].map((it) => (
              <div key={it.k} className="border-l-2 border-[#ED1C24] pl-4">
                <div className="text-xs uppercase tracking-wider text-gray-400">{it.k}</div>
                <div className="font-heading font-bold mt-1">{it.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#00A651] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading font-bold text-3xl sm:text-4xl">Let's get started.</h2>
          <p className="font-sub italic text-lg opacity-90 mt-2">
            A quick application puts you in front of a real originator.
          </p>
          <Link
            to="/apply"
            data-testid="about-apply-btn"
            className="inline-block mt-6 bg-white text-[#00A651] hover:bg-black hover:text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
          >
            Apply Now
          </Link>
        </div>
      </section>
    </div>
  );
}
